// Data service - now powered by the CIBA backend API (MongoDB)
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let postsCache = null;
let pagesCache = null;

export function clearCache() {
  postsCache = null;
  pagesCache = null;
}

export async function getAllPosts() {
  if (postsCache) return postsCache;
  const res = await axios.get(`${API}/posts`);
  postsCache = res.data;
  return postsCache;
}

export async function getPostBySlug(slug) {
  try {
    const res = await axios.get(`${API}/posts/${slug}`);
    return res.data;
  } catch (e) {
    return null;
  }
}

export async function getPages() {
  if (pagesCache) return pagesCache;
  const res = await axios.get(`${API}/pages`);
  pagesCache = res.data;
  return pagesCache;
}

export async function getPageBySlug(slug) {
  try {
    const res = await axios.get(`${API}/pages/${slug}`);
    return res.data;
  } catch (e) {
    return null;
  }
}

export async function sendContactMessage(data) {
  const res = await axios.post(`${API}/contact`, data);
  return res.data;
}

export async function submitRegistration(data) {
  const res = await axios.post(`${API}/registrations`, data);
  return res.data;
}

export async function subscribeNewsletter(data) {
  const res = await axios.post(`${API}/subscribe`, data);
  return res.data;
}

export async function submitLetterRequest(data) {
  const res = await axios.post(`${API}/letter-requests`, data);
  return res.data;
}

export async function submitProgramRegistration(data) {
  const res = await axios.post(`${API}/program-registrations`, data);
  return res.data;
}

export async function getProgramAvailability(program) {
  const res = await axios.get(`${API}/program-registrations/availability?program=${program}`);
  return res.data;
}

// ---------- Admin ----------
export function getToken() {
  return localStorage.getItem('ciba_admin_token');
}

export function setToken(token) {
  if (token) localStorage.setItem('ciba_admin_token', token);
  else localStorage.removeItem('ciba_admin_token');
}

function authHeaders() {
  return { headers: { Authorization: `Bearer ${getToken()}` } };
}

export async function adminLogin(username, password) {
  const res = await axios.post(`${API}/auth/login`, { username, password });
  setToken(res.data.token);
  return res.data;
}

export async function adminVerify() {
  const res = await axios.get(`${API}/auth/me`, authHeaders());
  return res.data;
}

export async function adminCreatePost(data) {
  const res = await axios.post(`${API}/posts`, data, authHeaders());
  clearCache();
  return res.data;
}

export async function adminUpdatePost(id, data) {
  const res = await axios.put(`${API}/posts/${id}`, data, authHeaders());
  clearCache();
  return res.data;
}

export async function adminDeletePost(id) {
  const res = await axios.delete(`${API}/posts/${id}`, authHeaders());
  clearCache();
  return res.data;
}

export async function adminGetMessages() {
  const res = await axios.get(`${API}/contact`, authHeaders());
  return res.data;
}

export async function adminDeleteMessage(id) {
  const res = await axios.delete(`${API}/contact/${id}`, authHeaders());
  return res.data;
}

export async function adminGetRegistrations() {
  const res = await axios.get(`${API}/registrations`, authHeaders());
  return res.data;
}

export async function adminDeleteRegistration(id) {
  const res = await axios.delete(`${API}/registrations/${id}`, authHeaders());
  return res.data;
}

export async function adminGetSubscribers() {
  const res = await axios.get(`${API}/subscribers`, authHeaders());
  return res.data;
}

export async function adminDeleteSubscriber(id) {
  const res = await axios.delete(`${API}/subscribers/${id}`, authHeaders());
  return res.data;
}

export async function adminGetLetterRequests() {
  const res = await axios.get(`${API}/letter-requests`, authHeaders());
  return res.data;
}

export async function adminDeleteLetterRequest(id) {
  const res = await axios.delete(`${API}/letter-requests/${id}`, authHeaders());
  return res.data;
}

export async function adminGetPages() {
  const res = await axios.get(`${API}/pages`);
  return res.data;
}

export async function adminUpdatePage(slug, data) {
  const res = await axios.put(`${API}/pages/${slug}`, data, authHeaders());
  clearCache();
  return res.data;
}

export async function adminChangePassword(currentPassword, newPassword) {
  const res = await axios.post(
    `${API}/auth/change-password`,
    { current_password: currentPassword, new_password: newPassword },
    authHeaders()
  );
  return res.data;
}

export async function adminGetProgramRegistrations(program) {
  const params = program && program !== 'all' ? `?program=${program}` : '';
  const res = await axios.get(`${API}/program-registrations${params}`, authHeaders());
  return res.data;
}

export async function adminDeleteProgramRegistration(id) {
  const res = await axios.delete(`${API}/program-registrations/${id}`, authHeaders());
  return res.data;
}

export async function adminSendProgramNotice(program, subject, message) {
  const res = await axios.post(`${API}/program-registrations/notify`, { program, subject, message }, authHeaders());
  return res.data;
}

export async function submitIndividualRegistration(data) {
  const res = await axios.post(`${API}/individual-registrations`, data);
  return res.data;
}

export async function adminGetIndividualRegistrations() {
  const res = await axios.get(`${API}/individual-registrations`, authHeaders());
  return res.data;
}

export async function adminDeleteIndividualRegistration(id) {
  const res = await axios.delete(`${API}/individual-registrations/${id}`, authHeaders());
  return res.data;
}

export async function adminSendLeagueNotice(league, audience, subject, message) {
  const res = await axios.post(`${API}/league-notify`, { league, audience, subject, message }, authHeaders());
  return res.data;
}

export async function submitVolunteer(data) {
  const res = await axios.post(`${API}/volunteers`, data);
  return res.data;
}

export async function adminGetVolunteers() {
  const res = await axios.get(`${API}/volunteers`, authHeaders());
  return res.data;
}

export async function adminDeleteVolunteer(id) {
  const res = await axios.delete(`${API}/volunteers/${id}`, authHeaders());
  return res.data;
}

export async function adminSendVolunteerNotice(subject, message, league = '', area = '') {
  const res = await axios.post(`${API}/volunteers/notify`, { subject, message, league, area }, authHeaders());
  return res.data;
}

export async function getVolunteerSpotlight() {
  const res = await axios.get(`${API}/volunteer-spotlight`);
  return res.data;
}

export async function adminSetVolunteerSpotlight(data) {
  const res = await axios.put(`${API}/volunteer-spotlight`, data, authHeaders());
  return res.data;
}

export async function adminClearVolunteerSpotlight() {
  const res = await axios.delete(`${API}/volunteer-spotlight`, authHeaders());
  return res.data;
}

export async function getSpotlightArchive() {
  const res = await axios.get(`${API}/volunteer-spotlight/archive`);
  return res.data;
}

export async function adminDeleteSpotlightArchive(id) {
  const res = await axios.delete(`${API}/volunteer-spotlight/archive/${id}`, authHeaders());
  return res.data;
}

export async function getResultsLinks() {
  const res = await axios.get(`${API}/results-links`);
  return res.data;
}

export async function adminAddResultsLink(data) {
  const res = await axios.post(`${API}/results-links`, data, authHeaders());
  return res.data;
}

export async function adminDeleteResultsLink(id) {
  const res = await axios.delete(`${API}/results-links/${id}`, authHeaders());
  return res.data;
}

export function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

export const CATEGORIES = [
  { slug: 'latest-news', name: 'Latest news' },
  { slug: 'national-program', name: 'National Programs' },
  { slug: 'national-mens-league', name: "National Men's League" },
  { slug: 'national-womens-league', name: "National Women's League" },
  { slug: '3-on-3', name: '3 on 3' },
  { slug: 'basketball', name: 'Basketball' },
  { slug: 'under-19', name: 'Under 19' },
  { slug: 'under-16', name: 'Under 16' },
  { slug: 'under-14', name: 'Under 14' },
  { slug: 'u13', name: 'U13' },
  { slug: 'primary-school', name: 'Primary School' },
  { slug: 'basketball-academy', name: 'Basketball Academy' },
  { slug: 'coach-development', name: 'Coach Development' },
];
